import { IOneDriveRootDetails } from "../../../src/taskpane/interfaces/IOneDriveRootDetails";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { currentSharepointFolderUserPermissions } from "../../../src/taskpane/redux/models/reduxModels";
import { CurrentFolderPermissionState } from "./currentFolderPermissionState";
import {
  getDirectoryPermissionIdentity,
  getDirectoryRequestKey,
  isOneDriveDirectory,
} from "./contentManagerDirectoryIdentity";
import { createLoadCurrentFolderPermissionsDependencies } from "./createLoadCurrentFolderPermissionsDependencies";

export interface LoadCurrentFolderPermissionsDependencies {
  getDriveItemSharepointUniqueIdFn?: (driveId: string, itemId: string) => Promise<any>;
  getOneDriveAccessTokenFn?: () => Promise<string>;
  getSharePointAccessTokenFn?: () => Promise<string>;
  getUserEffectivePermissionsFn?: (
    accessToken: string,
    webUrl: string,
    sharePointListId?: string,
    sharePointUniqueId?: string
  ) => Promise<currentSharepointFolderUserPermissions>;
}

export class LoadCurrentFolderPermissionsError extends Error {
  public readonly state: CurrentFolderPermissionState;
  public readonly cause: Error;

  constructor(state: CurrentFolderPermissionState, cause: unknown) {
    super("Could not load folder permissions.");
    this.name = "LoadCurrentFolderPermissionsError";
    this.state = state;
    this.cause = cause instanceof Error
      ? cause
      : new Error(typeof cause === "string" ? cause : "Could not load folder permissions.");
  }
}

let defaultDependenciesCache: Required<LoadCurrentFolderPermissionsDependencies> | undefined;
const resolvedSharePointUniqueIdCache = new Map<string, string>();
const inFlightSharePointUniqueIdRequests = new Map<string, Promise<any>>();
const inFlightPermissionRequests = new Map<string, Promise<CurrentFolderPermissionState>>();

const getChildDirectoryCacheKey = (currentDirectory?: IPageSettings): string | undefined =>
  currentDirectory?.parentId && currentDirectory?.id
    ? `${currentDirectory.parentId}|${currentDirectory.id}`
    : undefined;

const getDefaultDependencies = (): Required<LoadCurrentFolderPermissionsDependencies> => {
  if (defaultDependenciesCache) {
    return defaultDependenciesCache;
  }

  const { getDriveItemSharepointUniqueId } = require("../../../src/taskpane/services/GraphService");
  const { getUserEffectivePermissions } = require("../../../src/taskpane/services/SharePointRestServices");
  const authModule = require("../../../utilities/AuthModule").default;

  defaultDependenciesCache = createLoadCurrentFolderPermissionsDependencies({
    authModule,
    getDriveItemSharepointUniqueIdFn: getDriveItemSharepointUniqueId,
    getUserEffectivePermissionsFn: getUserEffectivePermissions,
  });

  return defaultDependenciesCache;
};

export const clearLoadCurrentFolderPermissionsCache = (): void => {
  resolvedSharePointUniqueIdCache.clear();
  inFlightSharePointUniqueIdRequests.clear();
  inFlightPermissionRequests.clear();
};

export const loadCurrentFolderPermissions = async ({
  currentDirectory,
  oneDriveRootDetails,
  dependencies,
}: {
  currentDirectory?: IPageSettings;
  oneDriveRootDetails?: IOneDriveRootDetails;
  dependencies?: LoadCurrentFolderPermissionsDependencies;
}): Promise<CurrentFolderPermissionState> => {
  if (!currentDirectory) {
    return {
      status: "unknown",
    };
  }

  const baseIdentity = getDirectoryPermissionIdentity({
    currentDirectory,
    oneDriveRootDetails,
  });
  const directoryRequestKey = getDirectoryRequestKey({
    currentDirectory,
    oneDriveRootDetails,
  });

  if (isOneDriveDirectory(currentDirectory) && !oneDriveRootDetails?.sharePointSiteUrl) {
    return {
      directoryRequestKey,
      identityKey: baseIdentity.key,
      status: "loading",
    };
  }

  const resolvedDependencies: Required<LoadCurrentFolderPermissionsDependencies> = dependencies?.getDriveItemSharepointUniqueIdFn
    && dependencies?.getOneDriveAccessTokenFn
    && dependencies?.getSharePointAccessTokenFn
    && dependencies?.getUserEffectivePermissionsFn
    ? dependencies as Required<LoadCurrentFolderPermissionsDependencies>
    : { ...getDefaultDependencies(), ...dependencies };

  const {
    getDriveItemSharepointUniqueIdFn,
    getOneDriveAccessTokenFn,
    getSharePointAccessTokenFn,
    getUserEffectivePermissionsFn,
  } = resolvedDependencies;

  let resolvedSharePointUniqueId = currentDirectory?.sharePointUniqueId;
  let resolvedIdentity = baseIdentity;

  try {
    if (!baseIdentity.rootFolder && currentDirectory?.parentId && currentDirectory?.id && !resolvedSharePointUniqueId) {
      const childDirectoryCacheKey = getChildDirectoryCacheKey(currentDirectory);

      if (childDirectoryCacheKey && resolvedSharePointUniqueIdCache.has(childDirectoryCacheKey)) {
        resolvedSharePointUniqueId = resolvedSharePointUniqueIdCache.get(childDirectoryCacheKey);
      } else if (childDirectoryCacheKey) {
        let inFlightUniqueIdRequest = inFlightSharePointUniqueIdRequests.get(childDirectoryCacheKey);

        if (!inFlightUniqueIdRequest) {
          inFlightUniqueIdRequest = getDriveItemSharepointUniqueIdFn(currentDirectory.parentId, currentDirectory.id)
            .then((uniqueId: string | undefined | null) => {
              if (uniqueId) {
                resolvedSharePointUniqueIdCache.set(childDirectoryCacheKey, uniqueId);
              }

              return uniqueId;
            })
            .finally(() => {
              inFlightSharePointUniqueIdRequests.delete(childDirectoryCacheKey);
            });

          inFlightSharePointUniqueIdRequests.set(childDirectoryCacheKey, inFlightUniqueIdRequest);
        }

        resolvedSharePointUniqueId = await inFlightUniqueIdRequest;
      } else {
        resolvedSharePointUniqueId = await getDriveItemSharepointUniqueIdFn(currentDirectory.parentId, currentDirectory.id);
      }
    }

    resolvedIdentity = getDirectoryPermissionIdentity({
      currentDirectory,
      oneDriveRootDetails,
      resolvedSharePointUniqueId,
    });

    if (!resolvedIdentity.isComplete || !resolvedIdentity.sharePointListId || !resolvedIdentity.webUrl) {
      return {
        directoryRequestKey,
        identityKey: resolvedIdentity.key,
        resolvedSharePointUniqueId,
        status: "failed",
        errorMessage: "Could not resolve folder permission identity.",
      };
    }

    let inFlightPermissionRequest = inFlightPermissionRequests.get(resolvedIdentity.key);

    if (!inFlightPermissionRequest) {
      inFlightPermissionRequest = (async (): Promise<CurrentFolderPermissionState> => {
        const accessToken = resolvedIdentity.isOneDrive
          ? await getOneDriveAccessTokenFn()
          : await getSharePointAccessTokenFn();

        const permissions = await getUserEffectivePermissionsFn(
          accessToken,
          resolvedIdentity.webUrl,
          resolvedIdentity.sharePointListId,
          resolvedIdentity.rootFolder ? undefined : resolvedIdentity.sharePointUniqueId
        );

        return {
          directoryRequestKey,
          identityKey: resolvedIdentity.key,
          resolvedSharePointUniqueId,
          permissions: permissions ?? undefined,
          status: permissions ? "loaded" : "failed",
          errorMessage: permissions ? undefined : "Could not load folder permissions.",
        };
      })().finally(() => {
        inFlightPermissionRequests.delete(resolvedIdentity.key);
      });

      inFlightPermissionRequests.set(resolvedIdentity.key, inFlightPermissionRequest);
    }

    return await inFlightPermissionRequest;
  } catch (error) {
    throw new LoadCurrentFolderPermissionsError({
      directoryRequestKey,
      identityKey: resolvedIdentity.key,
      resolvedSharePointUniqueId,
      status: "failed",
      errorMessage: "Could not load folder permissions.",
    }, error);
  }
};
